import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Snowflake, LogOut, CalendarDays, Users, Clock3, GraduationCap, ExternalLink, Calculator, ChevronDown, Globe, HeartPulse } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { RINK_LABEL, RINK_BADGE, ROLE_LABEL, ROLE_BADGE } from "@/lib/rinks";

function NavDropdown({ label, icon: Icon, items, testId }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="font-mono-tight text-xs uppercase tracking-widest font-bold px-3 py-2 border-2 flex items-center gap-2 transition-colors bg-white text-slate-900 border-transparent hover:border-slate-900"
        data-testid={testId}
        aria-expanded={open}
      >
        <Icon className="w-4 h-4" /> {label} <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 min-w-[200px] bg-white border-2 border-slate-900 shadow-[4px_4px_0px_0px_#0f172a] z-50" data-testid={`${testId}-menu`}>
          {items.map((item) => item.newTab ? (
            <a
              key={item.label}
              href={item.to}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="flex items-center justify-between px-3 py-2.5 font-mono-tight text-xs uppercase tracking-widest font-bold hover:bg-sky-100 transition-colors border-b-2 border-slate-100 last:border-b-0"
              data-testid={`${testId}-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {item.label} <ExternalLink className="w-3 h-3" />
            </a>
          ) : (
            <NavLink
              key={item.label}
              to={item.to}
              onClick={() => setOpen(false)}
              className="block px-3 py-2.5 font-mono-tight text-xs uppercase tracking-widest font-bold hover:bg-sky-100 transition-colors border-b-2 border-slate-100 last:border-b-0"
              data-testid={`${testId}-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {item.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const navCls = ({ isActive }) =>
    `font-mono-tight text-xs uppercase tracking-widest font-bold px-3 py-2 border-2 flex items-center gap-2 transition-colors ${
      isActive ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-900 border-transparent hover:border-slate-900"
    }`;

  const isMedic = user.role === "medic";
  const rinkList = (user.role === "staff" || isMedic) ? [user.rink] : ["clonmel", "sligo"];
  const cap = (r) => r.charAt(0).toUpperCase() + r.slice(1);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 bg-white border-b-2 border-slate-900 px-4 md:px-6 py-3 flex items-center justify-between gap-4 flex-wrap shadow-[0px_4px_0px_0px_#0f172a]">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-sky-500 border-2 border-slate-900 shadow-[3px_3px_0px_0px_#0f172a] flex items-center justify-center">
            <Snowflake className="w-6 h-6 text-white" />
          </div>
          <div>
            <div className="font-display text-lg uppercase leading-none text-slate-900" data-testid="app-brand">
              On <span className="bg-sky-500 text-white px-1 -skew-x-3 inline-block">Ice</span> Bookings
            </div>
            <div className="font-mono-tight text-[10px] uppercase tracking-[0.25em] text-slate-500 mt-0.5">
              Sligo · Clonmel
            </div>
          </div>
        </div>

        <nav className="flex items-center gap-2 flex-wrap" data-testid="main-nav">
          {isMedic ? (
            <>
              <NavDropdown label="Medical" icon={HeartPulse} testId="nav-medical"
                items={[{ label: `${cap(user.rink)} Medical`, to: `/medical?rink=${user.rink}` }]} />
              <NavDropdown label="School Visits" icon={GraduationCap} testId="nav-school-visits"
                items={[{ label: `${cap(user.rink)} Schools`, to: `/school-schedule?rink=${user.rink}` }]} />
            </>
          ) : (
            <>
              <NavDropdown label="Group Bookings" icon={CalendarDays} testId="nav-group-bookings"
                items={rinkList.map((r) => ({ label: `${cap(r)} Groups`, to: `/?rink=${r}` }))} />
              <NavDropdown label="Schools Admin" icon={GraduationCap} testId="nav-schools"
                items={rinkList.map((r) => ({ label: `${cap(r)} Schools`, to: `/schools?rink=${r}` }))} />
              <NavDropdown label="Daily Summary" icon={Calculator} testId="nav-daily-summary"
                items={rinkList.map((r) => ({ label: `${cap(r)} Summary`, to: `/summary?rink=${r}` }))} />
              <NavDropdown label="School Booking Sites" icon={Globe} testId="nav-school-sites"
                items={rinkList.map((r) => ({ label: `${cap(r)} Booking`, to: `/schools/${r}`, newTab: true }))} />
              <NavDropdown label="Public Sessions" icon={Clock3} testId="nav-public-sessions"
                items={rinkList.map((r) => ({ label: `${cap(r)} Sessions`, to: `/sessions?rink=${r}` }))} />
              <NavDropdown label="Medical" icon={HeartPulse} testId="nav-medical"
                items={rinkList.map((r) => ({ label: `${cap(r)} Medical`, to: `/medical?rink=${r}` }))} />
              {user.role === "admin" && (
                <NavDropdown label="Users" icon={Users} testId="nav-users"
                  items={rinkList.map((r) => ({ label: `${cap(r)} Users`, to: `/users?rink=${r}` }))} />
              )}
            </>
          )}
        </nav>

        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono-tight text-xs font-bold text-slate-700 hidden md:inline" data-testid="current-user-name">
            {user.name || user.email}
          </span>
          <span className={`badge-brutal ${ROLE_BADGE[user.role]}`} data-testid="current-user-role">
            {ROLE_LABEL[user.role]}
          </span>
          {(user.role === "staff" || user.role === "medic") && user.rink && (
            <span className={`badge-brutal ${RINK_BADGE[user.rink]}`} data-testid="current-user-rink">
              {RINK_LABEL[user.rink]}
            </span>
          )}
          <button onClick={doLogout} className="btn-secondary !px-3" title="Sign out" data-testid="logout-button">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      <main>
        <Outlet />
      </main>
    </div>
  );
}
